import React, { useCallback, useEffect, useState } from 'react';
import {
    FormControl,
    FormControlLabel,
    Grid2,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Checkbox,
    TextField,
    Tooltip,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { MODAL_TYPE, useModals } from '../../hooks/useModal';
import {
    useGetTicketOption,
    addTicketOption,
    editTicketOption,
} from '@openapi/ticket-options/ticket-options';
import { useSlotTemplate } from '../../hooks/useSlotTemplate';
import { resolveSlotLabel } from '../../helpers/slotTemplate';
import useNotify from '../../hooks/useNotify';
import CommonButton from '../../components/atom/CommonButton';
import PageWithTitle from '../../components/atom/PageWithTitle';
import BlockWithTitle from '../../components/atom/BlockWithTitle';
import * as Yup from 'yup';
import { showsAutocomplete } from '../../autocomplete';
import AutocompleteSelect from '../../components/atom/AutoCompleteSelect';
import { useTranslation } from 'react-i18next';
import LanguageIcon from '@mui/icons-material/Language';
import { AGE_BAND_TYPES } from '../../helpers/constants';

function EditTicketOptionPage({ newTicketOption }) {
    const { t } = useTranslation();
    const { showId, ticketOptionId } = useParams();
    const { showModal } = useModals();
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [ticketOption, setTicketOption] = useState(null);
    const { notifyError, notifySuccess } = useNotify();

    const { template, isRentalShow } = useSlotTemplate(showId);

    const {
        data: ticketOptionData,
        isLoading: ticketOptionLoading,
        error: ticketOptionError,
    } = useGetTicketOption(Number(ticketOptionId), {
        query: {
            enabled: !newTicketOption && !!ticketOptionId,
        },
    });

    useEffect(() => {
        if (ticketOptionError) {
            navigate(`/shows/${showId}/ticketoptions`);
            notifyError(ticketOptionError);
        }
    }, [ticketOptionError, navigate, showId, notifyError]);

    const defaultTicketOption = {
        name: t('ticketoptions.defaultName'),
        nameI18n: {},
        description: t('ticketoptions.defaultDescription'),
        descriptionI18n: {},
        ageBand: null,
        guestVisible: true,
        showId,
        slotKey: null,
        venueId: null, // languages are not available for new ticket options, only edit
    };

    const loadTicketOption = useCallback(() => {
        if (ticketOptionData) {
            setTicketOption({
                id: ticketOptionData.id,
                name: ticketOptionData.name,
                nameI18n: ticketOptionData.nameI18n,
                description: ticketOptionData.description,
                descriptionI18n: ticketOptionData.descriptionI18n,
                ageBand: ticketOptionData.ageBand,
                guestVisible: ticketOptionData.guestVisible !== false,
                showId: ticketOptionData.showId,
                showTitle: ticketOptionData.showTitle,
                slotKey: ticketOptionData.slotKey ?? null,
                venueId: ticketOptionData.venueId,
            });
        }
    }, [ticketOptionData]);

    useEffect(() => {
        if (!newTicketOption) {
            loadTicketOption();
            setLoading(ticketOptionLoading);
        } else {
            setLoading(false);
        }
    }, [showId, ticketOptionId, loadTicketOption, newTicketOption, ticketOptionLoading]);

    const onEdit = (values) => {
        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('confirmationModal.title'),
            confirm: async () => {
                try {
                    await editTicketOption(Number(ticketOptionId), {
                        name: values.name,
                        nameI18n: values.nameI18n,
                        description: values.description,
                        descriptionI18n: values.descriptionI18n,
                        ageBand: values.ageBand,
                        guestVisible: values.guestVisible,
                        showId: parseInt(values.showId, 10),
                        slotKey: values.slotKey || null,
                    });
                    navigate(`/shows/${showId}/ticketoptions`);
                    notifySuccess(t('ticketoptions.notifications.updated'));
                } catch (err) {
                    notifyError(err);
                }
            },
        });
    };

    const onSubmit = async (values) => {
        if (!newTicketOption) {
            return onEdit(values);
        }
        try {
            await addTicketOption({
                name: values.name,
                nameI18n: values.nameI18n,
                description: values.description,
                descriptionI18n: values.descriptionI18n,
                ageBand: values.ageBand,
                guestVisible: values.guestVisible,
                showId: parseInt(values.showId, 10),
                slotKey: values.slotKey || null,
            });
            navigate(`/shows/${showId}/ticketoptions`);
            notifySuccess(t('ticketoptions.notifications.added'));
        } catch (error) {
            notifyError(error);
        }
    };

    if (loading) {
        return true;
    }

    const validateSchema = Yup.object().shape({
        name: Yup.string().required(t('common.required')),
        description: Yup.string().required(t('common.required')),
        showId: Yup.number().required(t('common.required')),
        ageBand: Yup.string().nullable(),
    });

    return (
        <Formik
            onSubmit={onSubmit}
            initialValues={ticketOption || { ...defaultTicketOption }}
            validationSchema={validateSchema}
        >
            {({ isSubmitting, values, setFieldValue, touched, errors }) => (
                <Form>
                    <PageWithTitle
                        title={
                            newTicketOption
                                ? t('ticketoptions.newTicketOptionName')
                                : `${ticketOption?.name}`
                        }
                    >
                        <Grid2 size={{ xs: 12, md: 8 }}>
                            <BlockWithTitle title={t('ticketoptions.title')}>
                                <Grid2 size={{ xs: 10, sm: 11 }}>
                                    <TextField
                                        fullWidth
                                        label={t('ticketoptions.columns.name')}
                                        value={values.name}
                                        onChange={(ev) => setFieldValue('name', ev.target.value)}
                                        error={touched.name && Boolean(errors.name)}
                                        helperText={touched.name ? errors.name : ''}
                                    />
                                </Grid2>
                                <Grid2
                                    size={{ xs: 2, sm: 1 }}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Tooltip title={t('common.translations')}>
                                        <IconButton
                                            onClick={() => {
                                                showModal(MODAL_TYPE.TRANSLATIONS, {
                                                    translations: values.nameI18n,
                                                    setTranslations: (translations) =>
                                                        setFieldValue('nameI18n', translations),
                                                    item: 'ticketoptions',
                                                    column: 'name',
                                                    venueId: values.venueId,
                                                    originalString: values.name,
                                                });
                                            }}
                                            disabled={!values.venueId}
                                            size="small"
                                            edge="end"
                                        >
                                            <LanguageIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Grid2>
                                <Grid2 size={{ xs: 10, sm: 11 }}>
                                    <TextField
                                        fullWidth
                                        label={t('ticketoptions.columns.description')}
                                        value={values.description}
                                        onChange={(ev) =>
                                            setFieldValue('description', ev.target.value)
                                        }
                                        error={touched.description && Boolean(errors.description)}
                                        helperText={touched.description ? errors.description : ''}
                                    />
                                </Grid2>
                                <Grid2
                                    size={{ xs: 2, sm: 1 }}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Tooltip title={t('common.translations')}>
                                        <IconButton
                                            onClick={() => {
                                                showModal(MODAL_TYPE.TRANSLATIONS, {
                                                    translations: values.descriptionI18n,
                                                    setTranslations: (translations) =>
                                                        setFieldValue(
                                                            'descriptionI18n',
                                                            translations,
                                                        ),
                                                    item: 'ticketoptions',
                                                    column: 'description',
                                                    venueId: values.venueId,
                                                    originalString: values.description,
                                                });
                                            }}
                                            disabled={!values.venueId}
                                            size="small"
                                            edge="end"
                                        >
                                            <LanguageIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Grid2>
                                <Grid2 size={{ xs: 12 }}>
                                    <FormControl fullWidth>
                                        <InputLabel id="age-band-type-label">
                                            {t('ticketoptions.columns.ageBand')}
                                        </InputLabel>
                                        <Select
                                            sx={{ textAlign: 'left' }}
                                            labelId="age-band-type-label"
                                            label={t('ticketoptions.columns.ageBand')}
                                            value={values.ageBand || null}
                                            displayEmpty
                                            onChange={(ev) => {
                                                setFieldValue(
                                                    'ageBand',
                                                    ev.target.value === '' ? null : ev.target.value,
                                                );
                                            }}
                                        >
                                            <MenuItem value="">
                                                {t('ticketoptions.ageBand.notDefined')}
                                            </MenuItem>
                                            {Object.values(AGE_BAND_TYPES).map((type) => (
                                                <MenuItem key={type} value={type}>
                                                    {type.charAt(0).toUpperCase() + type.slice(1)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid2>
                                {isRentalShow && (
                                    <Grid2 size={{ xs: 12 }}>
                                        <FormControl fullWidth>
                                            <InputLabel id="timeslot-period-label" shrink>
                                                {t('ticketoptions.columns.timeslotPeriod')}
                                            </InputLabel>
                                            <Select
                                                sx={{ textAlign: 'left' }}
                                                labelId="timeslot-period-label"
                                                label={t('ticketoptions.columns.timeslotPeriod')}
                                                notched
                                                value={values.slotKey || ''}
                                                displayEmpty
                                                onChange={(ev) => {
                                                    setFieldValue(
                                                        'slotKey',
                                                        ev.target.value === ''
                                                            ? null
                                                            : ev.target.value,
                                                    );
                                                }}
                                            >
                                                <MenuItem value="">
                                                    {t('ticketoptions.timeslotPeriod.allPeriods')}
                                                </MenuItem>
                                                {template.map((slot) => (
                                                    <MenuItem key={slot.key} value={slot.key}>
                                                        {resolveSlotLabel(template, slot)}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid2>
                                )}
                                <Grid2 size={{ xs: 12 }} display="flex">
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!values.guestVisible}
                                                onChange={(e) =>
                                                    setFieldValue(
                                                        'guestVisible',
                                                        !!e.target.checked,
                                                    )
                                                }
                                            />
                                        }
                                        label={t('ticketoptions.columns.guestVisible')}
                                        labelPlacement="end"
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12 }}>
                                    <AutocompleteSelect
                                        initCall={showsAutocomplete.init}
                                        autocompleteCall={showsAutocomplete.search}
                                        value={values?.showId}
                                        setValue={(ev) => setFieldValue('showId', ev)}
                                        disabled={true}
                                    >
                                        <TextField label={t('shows.model')} required />
                                    </AutocompleteSelect>
                                </Grid2>
                                <Grid2 size={{ xs: 6, md: 2 }}>
                                    <CommonButton
                                        fullWidth
                                        onClick={() => navigate(`/shows/${showId}/ticketoptions`)}
                                        label={t('common.cancel')}
                                        buttonType="secondary"
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 6, md: 2 }}>
                                    <CommonButton
                                        fullWidth
                                        disabled={isSubmitting}
                                        type="submit"
                                        label={t('common.save')}
                                        buttonType="primary"
                                    />
                                </Grid2>
                            </BlockWithTitle>
                        </Grid2>
                    </PageWithTitle>
                </Form>
            )}
        </Formik>
    );
}

export default EditTicketOptionPage;
